import { getStoredMerchantPosBranchId } from "@/modules/merchant/presentation/lib/merchant-pos-branch-storage";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type {
  AddWorkshopJobLaborDTO,
  AddWorkshopJobPartDTO,
  CreateWorkshopCustomerDTO,
  CreateWorkshopJobDTO,
  CreateWorkshopVehicleDTO,
  PatchWorkshopJobPartDTO,
  SetWorkshopJobStatusDTO,
  UpdateWorkshopJobDTO,
  WorkshopCheckoutDTO,
  WorkshopCustomerListQueryDTO,
  WorkshopFitmentSkusQueryDTO,
  WorkshopJobListQueryDTO,
  WorkshopJobStatusDTO,
  WorkshopLaborPresetsQueryDTO,
  WorkshopVehicleListQueryDTO,
} from "../../domain/contracts";

function workshopBaseUrl(shopId: string) {
  return `${config.apiUrl}/shops/${shopId}/workshop`;
}

/** ສົ່ງ X-Branch-Id ໃຫ້ກົງກັບສາຂາ POS ທີ່ເລືອກ */
function workshopHeaders(
  shopId: string,
  extra?: Record<string, string>,
): Record<string, string> | undefined {
  const branchId = getStoredMerchantPosBranchId(shopId);
  const headers: Record<string, string> = { ...extra };
  if (branchId) headers["X-Branch-Id"] = branchId;
  return Object.keys(headers).length > 0 ? headers : undefined;
}

export type WorkshopCustomerRow = {
  id: string;
  shopId: string;
  name: string;
  phone: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type WorkshopVehicleRow = {
  id: string;
  shopId: string;
  customerId: string;
  plateNumber: string;
  vehicleConfigurationId: string | null;
  vin: string | null;
  frameNumber: string | null;
  engineCode: string | null;
  odometer: number | null;
  createdAt: string;
  updatedAt: string;
};

export type WorkshopJobQuoteSummary = {
  partsSubtotal: number;
  laborsSubtotal: number;
  grandTotal: number;
  partsCount: number;
  laborsCount: number;
};

export type WorkshopJobListRow = {
  id: string;
  shopId: string;
  branchId: string;
  jobNumber: string;
  status: WorkshopJobStatusDTO;
  quoteStatus: string;
  currency: string;
  complaint: string;
  odometer: number | null;
  customerId: string;
  customerName: string;
  customerPhone: string;
  vehicleId: string;
  plateNumber: string;
  vehicleConfigurationId: string | null;
  createdAt: string;
  updatedAt: string;
  quoteSummary?: WorkshopJobQuoteSummary;
};

export type WorkshopJobPartRow = {
  id: string;
  jobId: string;
  shopProductSkuId: string | null;
  name: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  currency: string;
  source: "stock" | "marketplace" | "customer_supplied";
  inventoryState: "reserved" | "consumed" | "released" | "none";
  marketplaceOrderId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type WorkshopJobLaborRow = {
  id: string;
  jobId: string;
  shopProductSkuId: string | null;
  name: string;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  currency: string;
  createdAt: string;
  updatedAt: string;
};

export type WorkshopJobDetail = WorkshopJobListRow & {
  workNotes: string | null;
  posSaleId: string | null;
  assignedStaffId: string | null;
  createdByUserId: string | null;
  partsUnlockedAt: string | null;
  vehicleVin: string | null;
  vehicleFrameNumber: string | null;
  vehicleEngineCode: string | null;
  parts?: WorkshopJobPartRow[];
  labors?: WorkshopJobLaborRow[];
  allowedNextStatuses: WorkshopJobStatusDTO[];
  canEditParts?: boolean;
};

export type WorkshopLaborPresetRow = {
  shopProductSkuId: string;
  sku: string;
  name: string;
  price: number;
  currency: string;
};

export type WorkshopQuotePrintLine = {
  section: "part" | "labor";
  name: string;
  qty: number;
  amountText: string;
  excludedFromTotal?: boolean;
};

export type WorkshopQuoteTotals = {
  partsSubtotal: number;
  laborsSubtotal: number;
  subtotal: number;
  taxPercent: number;
  taxTotal: number;
  grandTotal: number;
  currency: string;
  partsCount: number;
  laborsCount: number;
  quoteStatus: string;
  shareText: string;
  printLines: WorkshopQuotePrintLine[];
};

export type FitmentSkuRow = {
  shopProductSkuId: string;
  sku: string;
  barcode: string | null;
  name: string;
  price: number;
  currency: string;
  onHand: number;
  reserved: number;
  available: number;
  fitsConfiguration: boolean;
};

export const workshopApi = {
  listCustomers(shopId: string, query: WorkshopCustomerListQueryDTO) {
    const url = new URL(`${workshopBaseUrl(shopId)}/customers`);
    if (query.q) url.searchParams.set("q", query.q);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    return fetcher.get<{
      data: WorkshopCustomerRow[];
      meta: { total: number; limit: number; offset: number };
    }>(url.toString(), { headers: workshopHeaders(shopId) });
  },

  createCustomer(shopId: string, body: CreateWorkshopCustomerDTO) {
    return fetcher.post<WorkshopCustomerRow>(
      `${workshopBaseUrl(shopId)}/customers`,
      body,
      { headers: workshopHeaders(shopId) },
    );
  },

  listVehicles(shopId: string, query: WorkshopVehicleListQueryDTO) {
    const url = new URL(`${workshopBaseUrl(shopId)}/vehicles`);
    if (query.q) url.searchParams.set("q", query.q);
    if (query.customerId) url.searchParams.set("customerId", query.customerId);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    return fetcher.get<{
      data: WorkshopVehicleRow[];
      meta: { total: number; limit: number; offset: number };
    }>(url.toString(), { headers: workshopHeaders(shopId) });
  },

  createVehicle(shopId: string, body: CreateWorkshopVehicleDTO) {
    return fetcher.post<WorkshopVehicleRow>(
      `${workshopBaseUrl(shopId)}/vehicles`,
      body,
      { headers: workshopHeaders(shopId) },
    );
  },

  listJobs(shopId: string, query: WorkshopJobListQueryDTO) {
    const url = new URL(`${workshopBaseUrl(shopId)}/jobs`);
    url.searchParams.set("status", query.status ?? "board");
    // listKind ຕ້ອງສົ່ງໄປ backend — ຖ້າ "quotes" ຈຶ່ງຈະຄິດ quoteSummary (ຍອດລວມ) ໃຫ້
    url.searchParams.set("listKind", query.listKind ?? "board");
    if (query.quoteStatus)
      url.searchParams.set("quoteStatus", query.quoteStatus);
    url.searchParams.set("limit", String(query.limit ?? 100));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.dateFrom) url.searchParams.set("dateFrom", query.dateFrom);
    if (query.dateTo) url.searchParams.set("dateTo", query.dateTo);
    if (query.q) url.searchParams.set("q", query.q);
    if (query.branchId) url.searchParams.set("branchId", query.branchId);
    return fetcher.get<{
      data: WorkshopJobListRow[];
      meta: { total: number; limit: number; offset: number };
    }>(url.toString(), { headers: workshopHeaders(shopId) });
  },

  getJob(shopId: string, jobId: string) {
    return fetcher.get<WorkshopJobDetail>(
      `${workshopBaseUrl(shopId)}/jobs/${jobId}`,
      { headers: workshopHeaders(shopId) },
    );
  },

  createJob(shopId: string, body: CreateWorkshopJobDTO) {
    return fetcher.post<WorkshopJobDetail>(
      `${workshopBaseUrl(shopId)}/jobs`,
      body,
      { headers: workshopHeaders(shopId) },
    );
  },

  updateJob(shopId: string, jobId: string, body: UpdateWorkshopJobDTO) {
    return fetcher.patch<WorkshopJobDetail>(
      `${workshopBaseUrl(shopId)}/jobs/${jobId}`,
      body,
      { headers: workshopHeaders(shopId) },
    );
  },

  setJobStatus(shopId: string, jobId: string, body: SetWorkshopJobStatusDTO) {
    return fetcher.post<WorkshopJobDetail>(
      `${workshopBaseUrl(shopId)}/jobs/${jobId}/status`,
      body,
      { headers: workshopHeaders(shopId) },
    );
  },

  listVehicleMakes(shopId: string) {
    return fetcher.get<{ items: { id: string; name: string }[] }>(
      `${workshopBaseUrl(shopId)}/vehicle-makes`,
      { headers: workshopHeaders(shopId) },
    );
  },

  listVehicleModels(shopId: string, makeId: string) {
    const url = new URL(`${workshopBaseUrl(shopId)}/vehicle-models`);
    url.searchParams.set("makeId", makeId);
    return fetcher.get<{
      items: { id: string; makeId: string; name: string }[];
    }>(url.toString(), { headers: workshopHeaders(shopId) });
  },

  listVehicleConfigurations(shopId: string, modelId: string) {
    const url = new URL(`${workshopBaseUrl(shopId)}/vehicle-configurations`);
    url.searchParams.set("modelId", modelId);
    return fetcher.get<{
      items: {
        id: string;
        modelId: string;
        label: string;
        modelCode: string | null;
      }[];
    }>(url.toString(), { headers: workshopHeaders(shopId) });
  },

  getVehicleConfiguration(shopId: string, id: string) {
    const url = new URL(`${workshopBaseUrl(shopId)}/vehicle-configuration`);
    url.searchParams.set("id", id);
    return fetcher.get<{
      id: string;
      makeId: string;
      makeName: string;
      modelId: string;
      modelName: string;
      label: string;
      displayName: string;
    }>(url.toString(), { headers: workshopHeaders(shopId) });
  },

  listFitmentSkus(shopId: string, query: WorkshopFitmentSkusQueryDTO) {
    const url = new URL(`${workshopBaseUrl(shopId)}/fitment-skus`);
    url.searchParams.set("branchId", query.branchId);
    if (query.vehicleConfigurationId) {
      url.searchParams.set(
        "vehicleConfigurationId",
        query.vehicleConfigurationId,
      );
    }
    if (query.q) url.searchParams.set("q", query.q);
    if (query.fitmentOnly) url.searchParams.set("fitmentOnly", "true");
    url.searchParams.set("limit", String(query.limit ?? 50));
    return fetcher.get<{ data: FitmentSkuRow[] }>(url.toString(), {
      headers: workshopHeaders(shopId),
    });
  },

  setVehicleConfiguration(
    shopId: string,
    jobId: string,
    vehicleConfigurationId: string | null,
  ) {
    return fetcher.post<WorkshopJobDetail>(
      `${workshopBaseUrl(shopId)}/jobs/${jobId}/vehicle-configuration`,
      { vehicleConfigurationId },
      { headers: workshopHeaders(shopId) },
    );
  },

  addPart(shopId: string, jobId: string, body: AddWorkshopJobPartDTO) {
    return fetcher.post<{
      ok: true;
      part: WorkshopJobPartRow;
      jobStatus: string;
    }>(`${workshopBaseUrl(shopId)}/jobs/${jobId}/parts`, body, {
      headers: workshopHeaders(shopId),
    });
  },

  patchPart(
    shopId: string,
    jobId: string,
    partId: string,
    body: PatchWorkshopJobPartDTO,
  ) {
    return fetcher.patch<WorkshopJobPartRow>(
      `${workshopBaseUrl(shopId)}/jobs/${jobId}/parts/${partId}`,
      body,
      { headers: workshopHeaders(shopId) },
    );
  },

  removePart(shopId: string, jobId: string, partId: string) {
    return fetcher.delete<{ ok: true }>(
      `${workshopBaseUrl(shopId)}/jobs/${jobId}/parts/${partId}`,
      { headers: workshopHeaders(shopId) },
    );
  },

  listLaborPresets(
    shopId: string,
    query: Partial<WorkshopLaborPresetsQueryDTO> = {},
  ) {
    const url = new URL(`${workshopBaseUrl(shopId)}/labor-presets`);
    if (query.q) url.searchParams.set("q", query.q);
    url.searchParams.set("limit", String(query.limit ?? 50));
    return fetcher.get<{ data: WorkshopLaborPresetRow[] }>(url.toString(), {
      headers: workshopHeaders(shopId),
    });
  },

  addLabor(shopId: string, jobId: string, body: AddWorkshopJobLaborDTO) {
    return fetcher.post<{ ok: true; labor: WorkshopJobLaborRow }>(
      `${workshopBaseUrl(shopId)}/jobs/${jobId}/labors`,
      body,
      { headers: workshopHeaders(shopId) },
    );
  },

  removeLabor(shopId: string, jobId: string, laborId: string) {
    return fetcher.delete<{ ok: true }>(
      `${workshopBaseUrl(shopId)}/jobs/${jobId}/labors/${laborId}`,
      { headers: workshopHeaders(shopId) },
    );
  },

  getQuote(shopId: string, jobId: string) {
    return fetcher.get<WorkshopQuoteTotals>(
      `${workshopBaseUrl(shopId)}/jobs/${jobId}/quote`,
      { headers: workshopHeaders(shopId) },
    );
  },

  approveQuote(shopId: string, jobId: string) {
    return fetcher.post<{ ok: true; quoteStatus: string }>(
      `${workshopBaseUrl(shopId)}/jobs/${jobId}/quote/approve`,
      {},
      { headers: workshopHeaders(shopId) },
    );
  },

  checkout(
    shopId: string,
    jobId: string,
    body: WorkshopCheckoutDTO,
    idempotencyKey: string,
  ) {
    return fetcher.post<{
      ok: true;
      posSaleId: string;
      saleNumber: string;
      total: number;
      currency: string;
      idempotentReplay: boolean;
    }>(`${workshopBaseUrl(shopId)}/jobs/${jobId}/checkout`, body, {
      headers: workshopHeaders(shopId, {
        "Idempotency-Key": idempotencyKey,
      }),
    });
  },
};
